var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Timeouts } from '../framework/Timeouts';
export class NovastarHSeriesBase {
    constructor(_driverType, state, log, framework) {
        this._driverType = _driverType;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // static properties
        // other class properties and constructor
        this.powerOnBrightness = 74; // provided by Tritech based on Crestron driver
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.getDetected(tags);
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const resp = yield this.send(`[{"cmd":"R0100", "param0":0, "param1":0}]`, (resp) => {
                    return (resp === null || resp === void 0 ? void 0 : resp.ack) !== undefined ? resp : undefined;
                }, tags);
                return (_a = resp === null || resp === void 0 ? void 0 : resp.protoVersion) !== null && _a !== void 0 ? _a : 'unknown';
            }),
        };
        // As per Tritech's suggestion using the Crestron driver as a reference, we use the brightness
        // setting as a proxy for power. There doesn't seem to be a brightness query, so we implement
        // the control as write-only.
        this.power = {
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                const powerResp = (yield this.send(`[{"cmd": "W0410", "screenId": 0, "deviceId": 0, "brightness": ${power ? this.powerOnBrightness : 0}}]`, (resp) => {
                    var _a;
                    return ((_a = resp === null || resp === void 0 ? void 0 : resp.ack) !== null && _a !== void 0 ? _a : '').toUpperCase() === 'OK' ? resp : undefined;
                }, tags));
                return ((_b = powerResp === null || powerResp === void 0 ? void 0 : powerResp.ack) !== null && _b !== void 0 ? _b : '').toUpperCase() === 'OK' ? power : this.state.power.value;
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _c;
                const presetIndex = parseInt(value.slice(1), 10);
                const presetResp = (yield this.send(`[{"cmd": "W0605", "screenId": 0, "deviceId": 0, "presetId": ${presetIndex}}]`, (resp) => {
                    var _a;
                    return ((_a = resp === null || resp === void 0 ? void 0 : resp.ack) !== null && _a !== void 0 ? _a : '').toUpperCase() === 'OK' ? resp : undefined;
                }, tags));
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                if (((_c = presetResp === null || presetResp === void 0 ? void 0 : presetResp.ack) !== null && _c !== void 0 ? _c : '').toUpperCase() !== 'OK') {
                    this.log.warning(`received error: ${JSON.stringify(presetResp)}`);
                    return this.state.videoMatrixRoutes.values[key];
                }
                return value;
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.getStatus(['poll']);
                }),
            },
            {
                intervalMs: 30000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    const detected = yield this.getDetected(['poll', 'get.detected']);
                    this.state.detected.update(detected);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.state.busy.update(false);
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = (yield this.send(`[{"cmd":"R0500", "param0":0}]`, (resp) => {
                return (resp === null || resp === void 0 ? void 0 : resp.ack) !== undefined ? resp : undefined;
            }, ['onInitialize']));
            if (resp.ack === undefined) {
                return 'verifyFailed';
            }
            return 'connected';
        });
    }
    // other class methods
    getDetected(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const videoInputs = (yield this.queryPresetList(tags)).map((preset, i) => {
                var _a;
                return ({
                    code: `a${preset.presetId}`,
                    external: false,
                    name: (_a = preset.name) !== null && _a !== void 0 ? _a : `Preset ${i + 1}`,
                });
            });
            const videoOutputs = [{ code: 'a1', name: 'Output' }];
            return { videoInputs, videoOutputs };
        });
    }
    getStatus(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.send(`[{"cmd":"R0500", "param0":0, "param1":0}]`, (resp) => {
                return (resp === null || resp === void 0 ? void 0 : resp.ack) !== undefined ? resp : undefined;
            }, tags);
        });
    }
    queryPresetList(tags) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const presetList = [];
            const presetMatch = (yield this.send(`[{"cmd":"R0600", "param0":0, "param1":0}]`, (resp) => {
                return (resp === null || resp === void 0 ? void 0 : resp.presets) !== undefined ? resp : undefined;
            }, tags, { sendTimeoutMs: 6000 }));
            const presets = (_a = presetMatch === null || presetMatch === void 0 ? void 0 : presetMatch.presets) !== null && _a !== void 0 ? _a : [];
            for (const preset of presets) {
                presetList.push({ presetId: preset.presetId, name: preset.name });
            }
            return presetList;
        });
    }
}
